import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useTraining } from '../../context/TrainingContext';
import CompetencyRadar from '../../components/training/CompetencyRadar';
import SessionCard from '../../components/training/SessionCard';
import trainingApi from '../../services/trainingApi';

const StudentDashboard = () => {
  const { user } = useTraining();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setIsLoading(true);
        const [sessionsRes, progressRes, assignmentsRes, cohortsRes] = await Promise.all([
          trainingApi.listSessions({ limit: 5, sort: '-startedAt' }).catch(() => ({ data: { sessions: [] } })),
          trainingApi.getMyProgress().catch(() => ({ data: null })),
          trainingApi.listAssignments().catch(() => ({ data: { assignments: [] } })),
          trainingApi.listCohorts().catch(() => ({ data: { cohorts: [] } })),
        ]);

        // Unwrap axios response → API body → data payload
        const unwrap = (res) => {
          const body = res?.data || res || {};
          return body.data || body;
        };

        const sessionsPayload = unwrap(sessionsRes);
        const sessionsList = Array.isArray(sessionsPayload.sessions) ? sessionsPayload.sessions :
                            Array.isArray(sessionsPayload) ? sessionsPayload : [];
        setSessions(sessionsList);

        const progressPayload = unwrap(progressRes);
        setProgress(progressPayload);

        const assignmentsPayload = unwrap(assignmentsRes);
        const assignmentsList = Array.isArray(assignmentsPayload.assignments) ? assignmentsPayload.assignments :
                               Array.isArray(assignmentsPayload) ? assignmentsPayload : [];
        setAssignments(assignmentsList);

        const cohortsPayload = unwrap(cohortsRes);
        const cohortsList = Array.isArray(cohortsPayload.cohorts) ? cohortsPayload.cohorts :
                           Array.isArray(cohortsPayload) ? cohortsPayload : [];
        setCohorts(cohortsList);
      } catch (err) {
        console.error('[StudentDashboard] Error loading data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const handleViewFeedback = (sessionId) => {
    navigate(`/training/session/${sessionId}/feedback`);
  };

  const handleContinueSession = (sessionId) => {
    navigate(`/training/session/${sessionId}`);
  };

  const handleStartAssignment = async (assignment) => {
    try {
      const scenarioId = assignment.scenarioIds?.[0]?._id || assignment.scenarioIds?.[0];
      if (!scenarioId) {
        setError('No scenario assigned');
        return;
      }
      const cohortId = assignment.cohortId?._id || assignment.cohortId;
      const organisationId = assignment.organisationId?._id || assignment.organisationId;
      const response = await trainingApi.startSession(scenarioId, {
        assignmentId: assignment._id,
        cohortId,
        organisationId,
        mode: assignment.mode || 'assessment',
      });
      const body = response.data || response;
      const data = body.data || body;
      const session = data.session || data;
      navigate(`/training/session/${session._id}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to start session');
    }
  };

  const handleJoinCohort = async () => {
    if (!joinCode.trim()) return;
    try {
      setJoining(true);
      const res = await trainingApi.joinCohort(joinCode.trim());
      const body = res.data || res;
      const data = body.data || body;
      setJoinDialogOpen(false);
      setJoinCode('');
      // Reload dashboard
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to join cohort');
    } finally {
      setJoining(false);
    }
  };

  const filteredAssignments = selectedCohort === 'all'
    ? assignments
    : assignments.filter(a => (a.cohortId?._id || a.cohortId) === selectedCohort);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const overallProgress = progress?.overallProgress;

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Welcome header */}
      <Typography variant="h4" gutterBottom>
        Welcome, {user?.name || 'Student'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Training Mode -- Simulated Patient Interviews
      </Typography>

      {/* Cohort selector + join */}
      {cohorts.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <TextField
            select size="small" label="Cohort" value={selectedCohort}
            onChange={e => setSelectedCohort(e.target.value)} sx={{ minWidth: 220 }}
          >
            <MenuItem value="all">All Cohorts</MenuItem>
            {cohorts.map(c => (
              <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
            ))}
          </TextField>
          <Button size="small" variant="outlined" onClick={() => setJoinDialogOpen(true)}>
            Join Cohort
          </Button>
        </Box>
      )}
      {cohorts.length === 0 && (
        <Box sx={{ mb: 3 }}>
          <Button size="small" variant="outlined" onClick={() => setJoinDialogOpen(true)}>
            Join a Cohort with Invitation Code
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        {/* Left column */}
        <Box>
          {/* Assignments */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Assignments</Typography>
            {filteredAssignments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                {cohorts.length === 0
                  ? 'Join a cohort to see your assignments. Use an invitation code from your lecturer.'
                  : 'No assignments yet. Your lecturer will assign scenarios for you to complete.'
                }
              </Typography>
            ) : filteredAssignments.map(assignment => (
                <Box
                  key={assignment._id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2">{assignment.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(assignment.scenarioIds || []).map(s => s.name || 'Scenario').join(', ')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Chip size="small" label={assignment.mode === 'assessment' ? 'GRADED' : 'Practice'}
                        color={assignment.mode === 'assessment' ? 'warning' : 'default'} variant="outlined" />
                      {assignment.dueDate && (
                        <Chip size="small" label={`Due: ${new Date(assignment.dueDate).toLocaleDateString()}`}
                          color={new Date(assignment.dueDate) < new Date() ? 'error' : 'default'} variant="outlined" />
                      )}
                      {assignment._myAttempts > 0 && (
                        <Chip size="small" label={`${assignment._myAttempts}/${assignment.maxAttempts} attempts`} variant="outlined" />
                      )}
                      {assignment.cohortId?.name && (
                        <Chip size="small" label={assignment.cohortId.name} variant="outlined" />
                      )}
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={assignment._canAttempt === false}
                    onClick={() => handleStartAssignment(assignment)}
                  >
                    {assignment._canAttempt === false ? 'Complete' : 'Start'}
                  </Button>
                </Box>
              ))
            }
          </Paper>

          {/* Recent sessions */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Recent Sessions</Typography>
              <Button size="small" onClick={() => navigate('/training/sessions')}>
                View All
              </Button>
            </Box>
            {sessions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No sessions yet. Start your first training scenario from the Scenario Library.
              </Typography>
            ) : (
              sessions.map(session => (
                <SessionCard
                  key={session._id}
                  session={session}
                  onViewFeedback={handleViewFeedback}
                  onContinue={handleContinueSession}
                />
              ))
            )}
          </Paper>
        </Box>

        {/* Right column */}
        <Box>
          {/* Stats */}
          {overallProgress && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>My Progress</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {overallProgress.sessionsCompleted || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Sessions</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {overallProgress.averageCompositeScore ? `${Math.round(overallProgress.averageCompositeScore)}%` : '--'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Avg Score</Typography>
                </Box>
              </Box>

              <CompetencyRadar
                data={progress?.competencyProfile}
                size="compact"
              />
            </Paper>
          )}

          {/* Quick access */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Quick Access</Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/training/scenarios')}
              sx={{ mb: 1 }}
            >
              Browse Scenario Library
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/training/progress')}
            >
              View Full Progress
            </Button>
          </Paper>
        </Box>
      </Box>

      {/* Join Cohort Dialog */}
      <Dialog open={joinDialogOpen} onClose={() => setJoinDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Join a Cohort</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the invitation code provided by your lecturer to join a training cohort.
          </Typography>
          <TextField
            autoFocus fullWidth label="Invitation Code" value={joinCode}
            onChange={e => setJoinCode(e.target.value.toUpperCase())}
            placeholder="e.g. AB12CD34"
            onKeyDown={e => { if (e.key === 'Enter') handleJoinCohort(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleJoinCohort} disabled={joining || !joinCode.trim()}>
            {joining ? 'Joining...' : 'Join'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentDashboard;
